import { create } from "zustand";

import { bfnKeepAliveCapable } from "@/config/bfnKeepAlive";

/**
 * Open/closed/mounted state for the BFN Calculator full-height view.
 *
 * `isOpen` drives visibility; `mounted` drives whether ChatPage keeps the
 * view (and its heavy iframe) in the tree. On keep-alive-capable devices
 * (see bfnKeepAlive.ts) closing only HIDES the calculator, so reopening is
 * instant instead of re-paying the ~7 s handshake + SPA boot; on low-end
 * devices close() fully unmounts, exactly the pre-keep-alive behavior.
 *
 * Deliberately NOT persisted: the calculator is a transient full-screen
 * surface, so a reload always returns to the normal chat layout and the view
 * can never get "stuck open" across sessions (unlike the persisted nav state).
 */
type BfnCalculatorState = {
  isOpen: boolean;
  mounted: boolean;
  open: () => void;
  close: () => void;
  /** Full teardown: idle timeout, non-ready close, logout (never leak a
   *  previous account's iframe/session), low-end devices. */
  unmount: () => void;
};

export const useBfnCalculatorState = create<BfnCalculatorState>((set) => ({
  isOpen: false,
  mounted: false,
  open: () => set({ isOpen: true, mounted: true }),
  close: () =>
    set((state) => ({
      isOpen: false,
      mounted: state.mounted && bfnKeepAliveCapable(),
    })),
  unmount: () => set({ isOpen: false, mounted: false }),
}));
